import { BaseAutocomplete as Autocomplete } from '@pcln/horizon'
import { useMachine } from '@xstate/react'
import CONFIG from 'isomorphic-config'
import fetch from 'isomorphic-unfetch'
import { forwardRef, useCallback, useEffect, useMemo, useRef, useState, type SetStateAction } from 'react'

import fireTypeaheadClickEvent, { fireAutocompleteOpenEvent } from '@/components/TypeAhead/ga4'
import useTypeAhead from '@/components/TypeAhead/useTypeAhead'
import useBootstrapData from '@/hooks/useBootstrapData'
import useClientSideSignedInData from '@/hooks/useClientSideSignedInData'
import useRecentSearchesFetched from '@/hooks/useRecentSearchesFetched'
import useRecentSearchesLocalStorage from '@/hooks/useRecentSearchesLocalStorage'
import useRecommendations from '@/hooks/useRecommendations'
import useRecommendedCityIds from '@/hooks/useRecommendedCityIds'
import useSeti from '@/hooks/useSeti'
import analytics from '@/shared-utils/analytics'
import type { LOCATION_SEARCH_TYPE } from '@/types'

import geoMachine from '../../machines/currentLocationMachine'
import type { CityFilterItem, SelectedTopFilters } from '../Hotels/types'
import { DEFAULT_COLLISION_AVOIDANCE } from '../SearchForm/constants'
import { top20Filters } from '../TypeAhead/constants'
import {
  HTL_TYPEAHEAD_POPULAR_TRENDING_EXPERIMENT,
  isHotelTypeaheadEndpoint,
} from '../TypeAhead/constants/popularTrending'
import { renderDisplayText } from '../TypeAhead/Dropdown/RenderCurrentLocation'
import { fireFilterBarChipGA4Event } from '../TypeAhead/FilterBar/ga4'
import type { TypeAheadConfigEndpoint } from '../TypeAhead/types.ts'
import { useFlightAirportNesting } from '../TypeAhead/useFlightAirportNesting'
import { useHotelNeighbourhoodNesting } from '../TypeAhead/useHotelNeighbourhoodNesting'
import { usePopularTrendingImpression } from '../TypeAhead/usePopularTrendingImpression'
import { cityIdExistsInTop20Filters } from '../TypeAhead/utils'
import { resolveTypeaheadItemName } from '../TypeAhead/utils/resolveTypeaheadItemName'
import { mapTypeToCode, recentSearchesKey } from './constants'
import { getHotelTypeaheadFirstLineSuffix } from './getHotelTypeaheadFirstLineSuffix'
import {
  buildAutocompleteDataAndListType,
  excludeNeighbourhoodRecentSearches,
  hasRecentSearches,
  hasRecommendedSearches,
  mergeRecentSearchesById,
  resolveClickListTypeForItem,
} from './utils/autocompleteData'

const MAX_AUTOCOMPLETE_RECENT_SEARCHES = 3

const AI_SEARCH_BUTTON_CONFIG = {
  VARIANT1: { label: 'Ask', linkName: 'ask_ai_mode_typeahead' },
  VARIANT2: { label: 'AI Mode', linkName: 'ai_mode_typeahead' },
} as const

/**
 * Resolves the `select_content` analytics values for a typeahead selection: the
 * canonical `searchType` and, for hotels, the structured `item_name`
 * (`[chip]_[entity]_[search-type]`). Extracted from `handleSelectItem` to keep
 * that callback under the cognitive-complexity budget.
 */
function resolveTypeaheadSelectAnalytics({
  selectedLocation,
  resolveClickType,
  isHotelTypeahead,
  popularTrendingVariant,
}: {
  selectedLocation: LOCATION_SEARCH_TYPE
  resolveClickType: string | undefined
  isHotelTypeahead: boolean
  popularTrendingVariant: string | undefined
}): { itemName: string | undefined; searchType: string | undefined } {
  const searchType = selectedLocation.type === 'AI_QUERY' ? 'typein_ai_query' : resolveClickType
  const itemName = resolveTypeaheadItemName({
    item: selectedLocation,
    isHotelTypeahead,
    popularTrendingVariant,
    searchType,
  })
  return { itemName, searchType }
}

function getSelectedChildPosition({
  items,
  selectedId,
  parentId,
}: {
  items: LOCATION_SEARCH_TYPE[]
  selectedId: string
  parentId?: string
}): number | null {
  const siblings = items.filter((item) => item.parentId === parentId)
  const index = siblings.findIndex((item) => item.id === selectedId)
  return index === -1 ? null : index
}

function resolveSelectedChildPosition({
  items,
  selectedId,
  parentId,
  isIndented,
  typeAheadEndpoint,
  neighbourhoodNestingEnabled,
}: {
  items: LOCATION_SEARCH_TYPE[]
  selectedId: string
  parentId?: string
  isIndented?: boolean
  typeAheadEndpoint: TypeAheadConfigEndpoint
  neighbourhoodNestingEnabled: boolean
}): number | null {
  const supportsChildPosition =
    typeAheadEndpoint === 'flights' || (typeAheadEndpoint === 'hotelsV2' && neighbourhoodNestingEnabled)
  if (!isIndented || !parentId || !supportsChildPosition) {
    return null
  }
  return getSelectedChildPosition({ items, selectedId, parentId })
}

export type AutocompleteWrapperOnItemSelectContext = {
  inputElement?: HTMLInputElement | null
  /** True when this call is a passive prefill, not a real user pick — consumers should
   * skip side effects meant for an actual selection (e.g. advancing focus). */
  isPrefill?: boolean
}

const GEO_MACHINE_INITIAL = {
  context: { error: null, cityData: null, position: null },
} as const

const AutocompleteWrapper = forwardRef<
  HTMLInputElement,
  {
    searchKey: string
    onItemSelect?: (
      item: LOCATION_SEARCH_TYPE | null,
      context?: AutocompleteWrapperOnItemSelectContext,
    ) => void
    onSelectItemName?: (itemName: string | null) => void
    initialSelectedItem?: LOCATION_SEARCH_TYPE
    typeAheadEndpoint?: TypeAheadConfigEndpoint
    errorMessage?: string
    label?: string
    isMobile?: boolean
    showFilterChips?: boolean
    selectedFilterChipData?: SelectedTopFilters
    setSelectedFilterChipData?: (value: SetStateAction<SelectedTopFilters>) => void
    showCurrentLocation?: boolean
    onGeolocationError?: (errorMessage: string | null) => void
    aiSearchButtonVariant?: 'VARIANT1' | 'VARIANT2'
    mergeRecentAndRecommendedSearches?: boolean
    preferRecentSearchesOnly?: boolean
    onRecentSearchImprovementImpression?: () => void
    /** Hotels Horizon form only — defers neighbourhood nesting on other hotel endpoint consumers (e.g. Packages). */
    enableHotelNeighbourhoodNesting?: boolean
    hasAISearch?: boolean
    hasLTRSearch?: boolean
    /** Desktop-only experiment: prefill (pre-select) the field with the user's last searched city instead of leaving it empty. */
    enablePrefillFromLastSearch?: boolean
  }
>(function AutocompleteWrapper(
  {
    searchKey,
    typeAheadEndpoint = 'hotels',
    onItemSelect,
    onSelectItemName,
    initialSelectedItem,
    errorMessage,
    label,
    isMobile,
    showFilterChips = false,
    selectedFilterChipData,
    setSelectedFilterChipData,
    showCurrentLocation = false,
    onGeolocationError,
    aiSearchButtonVariant,
    mergeRecentAndRecommendedSearches = false,
    preferRecentSearchesOnly = false,
    onRecentSearchImprovementImpression,
    enableHotelNeighbourhoodNesting = false,
    hasAISearch = false,
    hasLTRSearch = false,
    enablePrefillFromLastSearch = false,
  },
  ref,
) {
  const {
    webstats: { clientGUID },
    appName,
    appVersion,
    signInStatus: { signedIn: serverSignedIn },
  } = useBootstrapData()
  const { signedIn: clientSideSignedIn } = useClientSideSignedInData()
  const signedIn = serverSignedIn || clientSideSignedIn
  const isHotelTypeahead = isHotelTypeaheadEndpoint(typeAheadEndpoint)
  const popularTrendingVariant = useSeti({
    experimentCode: HTL_TYPEAHEAD_POPULAR_TRENDING_EXPERIMENT,
    shouldFireImpression: false,
  })

  const [current, send] = useMachine(geoMachine, GEO_MACHINE_INITIAL)

  const displayFilterChips = !hasAISearch && showFilterChips

  // recent search
  const currentRecentSearchesKey = recentSearchesKey[typeAheadEndpoint]
  const [recentStartLocations, recentEndLocations] = useRecentSearchesLocalStorage(currentRecentSearchesKey)
  const recentSearchesLocalStorage = searchKey.includes('startLocation')
    ? recentStartLocations
    : recentEndLocations
  const [recentSearchesFetched, areRecentSearchesReady] = useRecentSearchesFetched(
    clientGUID,
    appName,
    appVersion,
    signedIn,
  )
  const isHotelEndpoint = typeAheadEndpoint === 'hotels' || typeAheadEndpoint === 'hotelsV2'
  const isHotelMergeRecentSearchFlow = isHotelEndpoint && mergeRecentAndRecommendedSearches
  const useImprovedRecentSearchFlow =
    isHotelEndpoint && (mergeRecentAndRecommendedSearches || preferRecentSearchesOnly)
  const recentSearches = useMemo(() => {
    const filterHotelRecentSearches = (items: LOCATION_SEARCH_TYPE[]) =>
      isHotelEndpoint ? excludeNeighbourhoodRecentSearches(items) : items

    if (useImprovedRecentSearchFlow) {
      if (!areRecentSearchesReady) {
        return []
      }
      if (hasRecentSearches(recentSearchesFetched)) {
        return mergeRecentSearchesById(
          filterHotelRecentSearches(recentSearchesFetched),
          filterHotelRecentSearches(recentSearchesLocalStorage),
          MAX_AUTOCOMPLETE_RECENT_SEARCHES,
        )
      }
      return filterHotelRecentSearches(recentSearchesLocalStorage).slice(0, MAX_AUTOCOMPLETE_RECENT_SEARCHES)
    }
    const recentsSource = isHotelEndpoint ? recentSearchesFetched : recentSearchesLocalStorage
    return filterHotelRecentSearches(recentsSource).slice(0, MAX_AUTOCOMPLETE_RECENT_SEARCHES)
  }, [
    isHotelEndpoint,
    useImprovedRecentSearchFlow,
    areRecentSearchesReady,
    recentSearchesFetched,
    recentSearchesLocalStorage,
  ])

  // Prefill (pre-select) the field with the user's last searched city so the
  // user can submit the form without interacting with this field at all —
  // not just show it as placeholder/label text. Sourced from `recentSearches`
  // (backed by useRecentSearchesFetched -> useRecentSearchCard ->
  // fetchRecentTripSearches), which already branches on signedIn to query by
  // auth token + cguid or by cguid alone — so it works for signed-out visitors
  // too, and matches the same list rendered in the Recent Searches dropdown.
  const [prefilledItem, setPrefilledItem] = useState<LOCATION_SEARCH_TYPE | undefined>(undefined)
  const didPrefillRef = useRef(false)
  // Recents can resolve after the user has already started interacting with
  // this field (focused it, typed, or picked something) — recentSearches from
  // the fetched (API) source in particular can arrive well after mount. Guard
  // against clobbering in-progress input; set from handleMenuOpen (focus/click)
  // and handleInputValueChange (typing) below.
  const hasInteractedRef = useRef(false)
  const effectiveInitialSelectedItem = prefilledItem ?? initialSelectedItem

  // recommended search
  const { recommendedCityIds } = useRecommendedCityIds({
    shouldFetchRecommendedCityIds: signedIn && !preferRecentSearchesOnly,
    typeAheadEndpoint,
  })

  const { data: recommendedSearches } = useRecommendations(recommendedCityIds)
  const displayRecommendedSearches = useMemo(
    () =>
      (isHotelMergeRecentSearchFlow && !areRecentSearchesReady) || preferRecentSearchesOnly
        ? []
        : recommendedSearches,
    [isHotelMergeRecentSearchFlow, areRecentSearchesReady, preferRecentSearchesOnly, recommendedSearches],
  )
  const [autocompleteValue, setAutocompleteValue] = useState<string | null>(null)
  const airportNestingEnabled = useFlightAirportNesting(typeAheadEndpoint)
  const neighbourhoodNestingEnabled = useHotelNeighbourhoodNesting({
    typeAheadEndpoint,
    enabled: enableHotelNeighbourhoodNesting,
  })
  const typeAheadHorizon = useTypeAhead({
    searchValue: autocompleteValue,
    searchKey,
    onLoaded: () => null,
    typeAheadEndpoint,
    airportNestingEnabled,
    neighbourhoodNestingEnabled,
    hasAISearch,
    hasLTRSearch,
  })

  type geoData = {
    cityID: string
    content: string
    itemName: string
    lat: number
    lon: number
    type: string
  }

  const geoData = current.context?.cityData as geoData | null

  const didFireSelect = useRef(false)
  const didFireRecentSearchImprovementImpression = useRef(false)
  const [autoKey, setAutoKey] = useState('auto-default')
  /** When user selects an item from the list, Base UI may call onInputValueChange with that value; skip updating autocompleteValue so we do not trigger a typeahead API call. */
  const lastSelectedItemNameRef = useRef<string | null>(null)
  /** Tracks the current input value (typed or selected) for use in onAiSearchClick.
   * Seeded from initialSelectedItem so pre-populated inputs (e.g. SEM landing pages)
   * carry the correct prompt when the user clicks "Ask" without typing first. */
  const currentSearchQueryRef = useRef<string>(initialSelectedItem?.itemName ?? '')

  /**
   * When geolocation resolves:
   * 1) Force a fresh <Autocomplete> instance by changing its `key`.
   *    The Horizon Autocomplete holds internal state (Downshift useCombobox + local useState),
   *    so a remount guarantees we clear input value, highlighted index, open/close state,
   *    and any pending suggestion state from before geo finished.
   * 2) Programmatically "select" the resolved city so the parent form receives it without
   *    requiring another user action.
   * 3) Guard with a ref so this runs once per resolution.
   *
   * Notes:
   * - Autocomplete manages internal items, selectedItem, overlay open state, and uses
   *   downshift’s useCombobox internally, not fully controlled from above—no single
   *   public “reset” API—hence the reliable `key`-flip remount.
   */

  useEffect(() => {
    if (!showCurrentLocation) return
    if (!current?.matches?.('resolved')) return
    const resolved = current.context?.cityData
    if (!resolved) return
    if (didFireSelect.current) return

    didFireSelect.current = true
    onItemSelect?.(resolved)
    // Defer setState to avoid synchronous setState in effect (cascading renders)
    const nextKey = `geo-${resolved.id ?? resolved.cityID}`
    queueMicrotask(() => {
      setAutoKey(nextKey)
    })
  }, [current, showCurrentLocation, onItemSelect])

  // Notify parent when geolocation fails so error can be displayed in form
  useEffect(() => {
    if (!showCurrentLocation || !onGeolocationError) return

    if (current.matches('rejected')) {
      const errorMsg = renderDisplayText(current, clientGUID)
      onGeolocationError(errorMsg)
    } else if (current.matches('idle') || current.matches('resolved')) {
      onGeolocationError(null)
    }
  }, [current, showCurrentLocation, clientGUID, onGeolocationError])

  /**
   * Prefill the field with the last searched city, once, when eligible:
   * 1) Force a fresh <Autocomplete> instance by changing its `key`, the same
   *    remount technique used for geolocation above — `defaultSelectedItem` is
   *    only read by Autocomplete on mount, so a value that arrives after the
   *    initial render (recentSearches loads asynchronously) needs a remount
   *    to actually populate the field.
   * 2) Notify the parent form via onItemSelect, same as a real user selection,
   *    so the field holds a real value the form can submit.
   * 3) Guard with a ref so this runs at most once per mount.
   */
  useEffect(() => {
    if (didPrefillRef.current) return
    if (!enablePrefillFromLastSearch || initialSelectedItem) return
    if (hasInteractedRef.current) return
    const lastSearchedItem = recentSearches[0]
    if (!lastSearchedItem) return

    didPrefillRef.current = true
    // Defer setState (and the parent notification) to avoid synchronous setState
    // in an effect (cascading renders), and to re-check interaction that may
    // have happened in the interim — recents can resolve well after mount.
    queueMicrotask(() => {
      if (hasInteractedRef.current) return
      currentSearchQueryRef.current = lastSearchedItem.itemName ?? ''
      // Mark as a prefill (not a real selection) so consumers skip side effects
      // meant for an actual user pick, like advancing focus to the next field.
      onItemSelect?.(lastSearchedItem, { isPrefill: true })
      setPrefilledItem(lastSearchedItem)
      setAutoKey(`prefill-${lastSearchedItem.id ?? lastSearchedItem.cityID}`)
    })
  }, [enablePrefillFromLastSearch, initialSelectedItem, recentSearches, onItemSelect])

  const { autocompleteData: baseAutocompleteData, listType } = useMemo(
    () =>
      buildAutocompleteDataAndListType({
        autocompleteValue,
        mergeRecentAndRecommendedSearches,
        preferRecentSearchesOnly,
        recentSearches,
        recommendedSearches: displayRecommendedSearches,
        typeAheadItems: typeAheadHorizon.items,
      }),
    [
      mergeRecentAndRecommendedSearches,
      preferRecentSearchesOnly,
      recentSearches,
      displayRecommendedSearches,
      typeAheadHorizon.items,
      autocompleteValue,
    ],
  )

  const autocompleteData = useMemo(() => {
    if (!(showCurrentLocation && isMobile)) return baseAutocompleteData
    const display = renderDisplayText(current, clientGUID)
    const currentLocationItem = {
      ...geoData,
      content: 'Current Location',
      displayLine1: display,
      itemName: geoData?.itemName || '',
      type: 'GEO',
    }
    return [currentLocationItem, ...baseAutocompleteData]
  }, [geoData, current, clientGUID, baseAutocompleteData, isMobile, showCurrentLocation])

  // Derive filterChipData directly (no useState/useEffect needed)
  const firstData = autocompleteData[0]
  const hasValidCity = firstData && 'cityID' in firstData && cityIdExistsInTop20Filters(firstData.cityID)
  const currentCityId = hasValidCity ? firstData.cityID : undefined
  const filterChipData = currentCityId ? top20Filters[currentCityId as keyof typeof top20Filters] : undefined

  // Track previous city to clear selected filters when city changes
  const prevCityIdRef = useRef<string | undefined>(undefined)

  useEffect(() => {
    const prevCityId = prevCityIdRef.current

    // Clear filters when switching cities (either to a different top20 or to non-top20)
    if (prevCityId !== undefined && prevCityId !== currentCityId) {
      setSelectedFilterChipData?.({
        amenities: new Set(),
        hotelStarLevel: new Set(),
        guestRating: new Set(),
        propertyTypes: new Set(),
        AMENITY: new Set(),
        HOTEL_STAR_LEVEL: new Set(),
        GUEST_RATING: new Set(),
        PROPERTY_TYPE: new Set(),
        ALL_BRANDS: new Set(),
        PROPERTY_THEME: new Set(),
      })
    }

    prevCityIdRef.current = currentCityId
  }, [currentCityId, setSelectedFilterChipData])

  const handleTypeAheadFeedback = useCallback(
    ({
      entered,
      id,
      itemName,
      displayName,
      type,
      selectedPosition,
      skipFeedbackCall,
      parentId,
      isIndented,
      selectedChildPosition,
    }: LOCATION_SEARCH_TYPE & {
      selectedPosition: number | null
      skipFeedbackCall?: boolean
      selectedChildPosition?: number | null
    }) => {
      if (skipFeedbackCall) return

      // swagger for feedback call api http://ny-htlindex-001:8080/typeahead/index/swagger-ui.html#/feedback-controller/typeaheadFeedbackWithSelectedPositionUsingGET
      const url = `${
        CONFIG.client.typeahead.feedback[typeAheadEndpoint]
      }/${id}/${encodeURIComponent(entered ?? 'null')}/${encodeURIComponent(
        itemName || displayName || '',
      )}/${selectedPosition ?? ''}`
      const queryParams = new URLSearchParams({
        language: 'en',
        locale: 'en-US',
        placeType: mapTypeToCode[type],
      })
      const shouldAppendNestingParams =
        typeAheadEndpoint === 'flights' ||
        (typeAheadEndpoint === 'hotelsV2' && neighbourhoodNestingEnabled && isIndented)

      if (shouldAppendNestingParams) {
        if (isIndented) {
          queryParams.set('isIndented', 'true')
        }
        if (parentId) {
          queryParams.set('parentId', parentId)
        }
        if (selectedChildPosition != null) {
          queryParams.set('selectedChildPosition', String(selectedChildPosition))
        }
      }

      const isHotel = typeAheadEndpoint === 'hotels' || typeAheadEndpoint === 'hotelsV2'
      if (isHotel) {
        void fetch(`${url}?${queryParams}`, {
          method: 'POST',
          headers: {
            'content-type': 'application/json',
          },
          body: JSON.stringify({
            items: typeAheadHorizon.items,
            apiDuration: typeAheadHorizon.duration,
            source: typeAheadHorizon.source,
          }),
        })
      } else {
        void fetch(`${url}?${queryParams}`)
      }
    },
    [
      typeAheadEndpoint,
      typeAheadHorizon.items,
      typeAheadHorizon.duration,
      typeAheadHorizon.source,
      neighbourhoodNestingEnabled,
    ],
  )

  const handleInputValueChange = useCallback(
    (inputValue: string) => {
      hasInteractedRef.current = true
      currentSearchQueryRef.current = inputValue
      if (lastSelectedItemNameRef.current !== null && inputValue === lastSelectedItemNameRef.current) {
        lastSelectedItemNameRef.current = null
        return
      }
      setAutocompleteValue(inputValue)
      if (inputValue === '') {
        onItemSelect?.(null)
        onSelectItemName?.(null)
      }
    },
    [onItemSelect, onSelectItemName],
  )

  type AutocompleteItem = (typeof autocompleteData)[number]

  const handleSelectItem = useCallback(
    (selectedItem: AutocompleteItem, highlightedIndex?: number) => {
      if ('isGroupHeader' in selectedItem && selectedItem.isGroupHeader) return

      if ('itemName' in selectedItem && typeof selectedItem.itemName === 'string') {
        lastSelectedItemNameRef.current = selectedItem.itemName
        currentSearchQueryRef.current = selectedItem.itemName
      }

      if ('content' in selectedItem && selectedItem.content === 'Current Location') {
        if (!geoData) send('REQUEST')
        else onItemSelect?.(selectedItem as LOCATION_SEARCH_TYPE)
        return
      }

      const selectedLocation = selectedItem as LOCATION_SEARCH_TYPE
      onItemSelect?.(selectedLocation)

      const { id: selectedId, parentId, isIndented } = selectedLocation
      const selectedChildPosition = resolveSelectedChildPosition({
        items: typeAheadHorizon.items,
        selectedId,
        parentId,
        isIndented,
        typeAheadEndpoint,
        neighbourhoodNestingEnabled,
      })

      const resolveClickType = resolveClickListTypeForItem(
        selectedItem as LOCATION_SEARCH_TYPE,
        listType,
        recentSearches,
        displayRecommendedSearches,
      )

      const { itemName: hotelItemName, searchType } = resolveTypeaheadSelectAnalytics({
        selectedLocation,
        resolveClickType,
        isHotelTypeahead,
        popularTrendingVariant,
      })
      fireTypeaheadClickEvent(typeAheadEndpoint, hotelItemName ?? searchType)
      onSelectItemName?.(hotelItemName ?? null)

      const hasCurrentLocation = showCurrentLocation && isMobile
      const adjustedIndex =
        hasCurrentLocation && highlightedIndex != null ? highlightedIndex - 1 : highlightedIndex

      handleTypeAheadFeedback({
        ...selectedLocation,
        selectedPosition: adjustedIndex ?? null,
        selectedChildPosition,
      })
    },
    [
      geoData,
      isMobile,
      listType,
      recentSearches,
      displayRecommendedSearches,
      onItemSelect,
      showCurrentLocation,
      typeAheadEndpoint,
      send,
      handleTypeAheadFeedback,
      typeAheadHorizon.items,
      isHotelTypeahead,
      popularTrendingVariant,
      onSelectItemName,
      neighbourhoodNestingEnabled,
    ],
  )

  const firePopularTrendingImpression = usePopularTrendingImpression(isHotelTypeahead)

  const handleMenuOpen = useCallback(() => {
    hasInteractedRef.current = true
    const hasHotelRecents = useImprovedRecentSearchFlow
      ? areRecentSearchesReady && hasRecentSearches(recentSearches)
      : hasRecentSearches(recentSearches)

    // HTL_SEARCH_FRM_RECENT_SEARCH_IMPRV: manual SETI impression on autocomplete open (not page load).
    // HorizonHotelForm sets useSeti shouldFireImpression: false and this experiment is in seti.json
    // noImpression — bucketing happens on load, exposure is recorded here when the dropdown opens.
    // Fires at most once per mount for signed-in users with recents or recommendations (same rule for all buckets).
    const shouldFireImpression = Boolean(
      onRecentSearchImprovementImpression &&
      !didFireRecentSearchImprovementImpression.current &&
      isHotelEndpoint &&
      signedIn &&
      (hasHotelRecents || hasRecommendedSearches(recommendedSearches)),
    )

    if (shouldFireImpression) {
      didFireRecentSearchImprovementImpression.current = true
      onRecentSearchImprovementImpression?.()
    }
    const updatedListType = listType === 'typein' && !autocompleteValue ? undefined : listType
    fireAutocompleteOpenEvent(typeAheadEndpoint, updatedListType)
    firePopularTrendingImpression()
  }, [
    onRecentSearchImprovementImpression,
    isHotelEndpoint,
    signedIn,
    useImprovedRecentSearchFlow,
    areRecentSearchesReady,
    recentSearches,
    recommendedSearches,
    listType,
    autocompleteValue,
    typeAheadEndpoint,
    firePopularTrendingImpression,
  ])

  type FilterChipClickItem = Parameters<
    NonNullable<React.ComponentProps<typeof Autocomplete>['handleFilterChipClick']>
  >[0]
  const handleFilterChipClick = useCallback(
    (item: FilterChipClickItem, selected: boolean) => {
      if (setSelectedFilterChipData) {
        setSelectedFilterChipData((prev: SelectedTopFilters) => {
          const { filterType, code, name } = item as CityFilterItem
          const newFilteredSet = new Set(prev[filterType])

          if (selected) {
            newFilteredSet.add(`${filterType}_${code}_${name}`)
            fireFilterBarChipGA4Event(filterType, name, true)
          } else {
            newFilteredSet.delete(`${filterType}_${code}_${name}`)
            fireFilterBarChipGA4Event(filterType, name, false)
          }
          return {
            ...prev,
            [filterType]: newFilteredSet,
          }
        })
      }
    },
    [setSelectedFilterChipData],
  )

  const memoizedSelectedFilterChipData = useMemo(() => {
    if (!selectedFilterChipData) return undefined
    return new Set([
      ...selectedFilterChipData.amenities,
      ...selectedFilterChipData.hotelStarLevel,
      ...selectedFilterChipData.guestRating,
      ...selectedFilterChipData.propertyTypes,
    ])
  }, [selectedFilterChipData])

  const onAiSearchClick = useCallback(() => {
    if (!aiSearchButtonVariant) return

    const { linkName } = AI_SEARCH_BUTTON_CONFIG[aiSearchButtonVariant]
    analytics.fireGA4Event({
      event: 'internal_link',
      attributes: {
        type: 'ai_mode_penny_search',
        link_name: linkName,
        page_name: 'homepage',
        product_name: 'penny',
      },
    })
    const prompt = currentSearchQueryRef.current
      ? encodeURIComponent(`Hotels near ${currentSearchQueryRef.current}`)
      : ''
    const query = prompt ? `?askPrompt=${prompt}` : ''
    window.open(`/penny${query}`, '_blank', 'noopener,noreferrer')
  }, [aiSearchButtonVariant])

  const rightIconButtonProps = useMemo(() => {
    if (!aiSearchButtonVariant) return undefined

    const { label: aiButtonLabel } = AI_SEARCH_BUTTON_CONFIG[aiSearchButtonVariant]
    return {
      children: <span className='text-primary-13'>{aiButtonLabel}</span>,
      onClick: onAiSearchClick,
      type: 'tertiary' as const,
      size: 'sm' as const,
      iconLeft: 'AiSearch' as const,
      slots: {
        textSlot: 'font-medium',
        iconSlot: 'relative -top-0.5',
      },
    }
  }, [aiSearchButtonVariant, onAiSearchClick])

  const getItemFirstLineSuffix = useCallback(
    (item: AutocompleteItem) =>
      getHotelTypeaheadFirstLineSuffix(item, { isHotelTypeahead, popularTrendingVariant }),
    [isHotelTypeahead, popularTrendingVariant],
  )

  return (
    <Autocomplete
      key={autoKey}
      inputRef={ref}
      errorMessage={errorMessage}
      label={label}
      items={autocompleteData}
      onInputValueChange={handleInputValueChange}
      onSelectItem={handleSelectItem}
      defaultSelectedItem={effectiveInitialSelectedItem}
      onMenuOpen={handleMenuOpen}
      filterChipData={displayFilterChips ? filterChipData : undefined}
      handleFilterChipClick={handleFilterChipClick}
      selectedFilterChipData={memoizedSelectedFilterChipData}
      collisionAvoidance={DEFAULT_COLLISION_AVOIDANCE}
      rightIconButtonProps={rightIconButtonProps}
      getItemFirstLineSuffix={isHotelTypeahead ? getItemFirstLineSuffix : undefined}
    />
  )
})

export default AutocompleteWrapper
